import { useState } from "react";
import { Button, Textarea, Toast } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import {
  embargoTextError,
  LAUNCH_DATE_TOKEN,
  marketingEmbargoTexts,
  type EmbargoText,
  type EmbargoTextKey,
} from "@/api/marketingEmbargoTexts.service";
import styles from "./EmbargoTexts.module.css";

/** A stand-in date, so the editor sees the sentence as a firm will read it. */
const SAMPLE_DATE = "1/10/2026";

const EDITORS: Array<{ key: EmbargoTextKey; title: string; explainer: string }> = [
  {
    key: "banner",
    title: "Banner above the table",
    explainer:
      "Shown to a firm when it filters the toolkit to an edition that has not launched yet.",
  },
  {
    key: "row_notice",
    title: "Notice on a ranking",
    explainer: "Shown inside an expanded row, above the badge files, for a ranking not yet public.",
  },
];

function EmbargoTextEditor({
  text,
  canWrite,
  onSaved,
  onError,
}: {
  text: EmbargoText;
  canWrite: boolean;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const editor = EDITORS.find(entry => entry.key === text.key);
  const [content, setContent] = useState(text.content);
  const [loadedContent, setLoadedContent] = useState(text.content);
  const [saving, setSaving] = useState(false);

  // A save elsewhere replaces what the editor holds. Done during render, not in an effect: an
  // effect that copied the prop in also ran once on mount, and React flushes pending effects
  // before it processes the first keystroke — so an edit made the moment the editor appeared
  // was overwritten with the loaded text (what the unit test did on a slow CI runner).
  if (loadedContent !== text.content) {
    setLoadedContent(text.content);
    setContent(text.content);
  }

  const contentError = embargoTextError(content);
  const changed = content.trim() !== text.content.trim();

  const save = async () => {
    setSaving(true);
    try {
      await marketingEmbargoTexts.save(text.key, content.trim());
      onSaved(`${editor?.title ?? "Text"} saved. It applies from the next page load.`);
    } catch (error) {
      onError(error instanceof Error ? error.message : "The text could not be saved");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.editor}>
      <h3 className={styles.title}>{editor?.title ?? text.key}</h3>
      <p className={styles.explainer}>{editor?.explainer}</p>

      <Textarea
        label="Wording"
        value={content}
        rows={3}
        disabled={!canWrite || saving}
        onChange={event => setContent(event.target.value)}
      />

      <p className={styles.explainer}>
        Write {LAUNCH_DATE_TOKEN} where the edition's launch date belongs. It is replaced before a
        firm sees the sentence.
      </p>

      {contentError !== null && <p className={styles.error}>{contentError}</p>}

      <p className={styles.preview}>{content.replaceAll(LAUNCH_DATE_TOKEN, SAMPLE_DATE)}</p>

      <div className={styles.actions}>
        <Button onClick={save} disabled={!canWrite || saving || contentError !== null || !changed}>
          {saving ? "Saving…" : "Save"}
        </Button>
        {text.updatedBy && (
          <span className={styles.savedAt}>
            Last changed by {text.updatedBy} on {new Date(text.updatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </section>
  );
}

export function EmbargoTextsPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.read");
  const canWrite = hasPermission("staff.marketing.write");
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { data, loading, error, refetch } = useQuery(() => marketingEmbargoTexts.get(), []);

  if (!canRead) {
    return <UnauthorizedView />;
  }

  const texts = data?.texts ?? [];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Embargo wording"
        subtitle="What a firm reads while an edition it holds has not launched yet"
      />

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading the wording…" />
      ) : (
        <div className={styles.editors}>
          {texts.map(text => (
            <EmbargoTextEditor
              key={text.key}
              text={text}
              canWrite={canWrite}
              onSaved={message => {
                setToast({ message, type: "success" });
                refetch();
              }}
              onError={message => setToast({ message, type: "error" })}
            />
          ))}
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default EmbargoTextsPage;
